import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';

const PLATFORM_ICONS = {
  twitter: 'X',
  facebook: 'FB',
  instagram: 'IG',
  linkedin: 'IN',
  tiktok: 'TT',
  pinterest: 'PI',
  youtube: 'YT'
};

const PLATFORM_COLORS = {
  twitter: 'border-white/30 text-white',
  facebook: 'border-blue-500/30 text-blue-400',
  instagram: 'border-pink-500/30 text-pink-400',
  linkedin: 'border-blue-400/30 text-blue-300',
  tiktok: 'border-cyan-500/30 text-cyan-400',
  pinterest: 'border-red-500/30 text-red-400',
  youtube: 'border-red-600/30 text-red-500'
};

export default function SocialMediaManager({ adminToken }) {
  const [accounts, setAccounts] = useState([]);
  const [availablePlatforms, setAvailablePlatforms] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [message, setMessage] = useState('');

  // Post-Formular
  const [postText, setPostText] = useState('');
  const [postImageUrl, setPostImageUrl] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  // Connect-Formular
  const [showConnect, setShowConnect] = useState(null);
  const [connectToken, setConnectToken] = useState('');
  const [connectName, setConnectName] = useState('');
  const [connectExtra, setConnectExtra] = useState({});

  useEffect(() => {
    loadAccounts();
    loadHistory();
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/accounts`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
        setAvailablePlatforms(data.available_platforms || {});
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/history?limit=20`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.posts || []);
      }
    } catch (err) {}
  };

  const handleConnect = async (platform) => {
    try {
      const res = await fetch(`${API_URL}/api/social/accounts/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          platform,
          access_token: connectToken,
          account_name: connectName || platform,
          ...connectExtra
        })
      });
      if (res.ok) {
        setMessage(`${availablePlatforms[platform]} verbunden`);
        setShowConnect(null);
        setConnectToken('');
        setConnectName('');
        setConnectExtra({});
        loadAccounts();
      }
    } catch (err) {
      setMessage('Verbindung fehlgeschlagen');
    }
  };

  const handleDisconnect = async (platform) => {
    try {
      await fetch(`${API_URL}/api/social/accounts/${platform}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setMessage(`${availablePlatforms[platform]} getrennt`);
      loadAccounts();
    } catch (err) {}
  };

  const handlePost = async () => {
    if (!postText.trim() || selectedPlatforms.length === 0) return;
    setPosting(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/social/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          text: postText,
          image_urls: postImageUrl ? [postImageUrl] : null,
          platforms: selectedPlatforms
        })
      });
      const data = await res.json();
      setMessage(data.summary || 'Gepostet');
      setPostText('');
      setPostImageUrl('');
      setSelectedPlatforms([]);
      loadHistory();
    } catch (err) {
      setMessage('Fehler beim Posten');
    } finally {
      setPosting(false);
    }
  };

  const togglePlatform = (p) => {
    setSelectedPlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  const connectedPlatforms = accounts.map(a => a.platform);

  if (loading) return <div className="text-white/60 p-8">Lade Social Media Manager...</div>;

  return (
    <div className="space-y-8 max-w-4xl">
      <h2 className="text-xl font-bold text-white">Social Media Manager</h2>

      {message && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm">
          {message}
        </div>
      )}

      {/* Verbundene Accounts */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-white/70 uppercase tracking-wider">Verbundene Plattformen</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(availablePlatforms).map(([key, label]) => {
            const connected = connectedPlatforms.includes(key);
            const account = accounts.find(a => a.platform === key);
            return (
              <div key={key} className={`bg-white/5 border rounded-lg p-4 text-center ${connected ? 'border-emerald-500/30' : 'border-white/10'}`}>
                <div className={`text-2xl font-bold mb-1 ${PLATFORM_COLORS[key] || 'text-white/60'}`}>
                  {PLATFORM_ICONS[key]}
                </div>
                <div className="text-xs text-white/60 mb-2">{label}</div>
                {connected ? (
                  <>
                    <div className="text-xs text-emerald-400 mb-2">{account?.account_name}</div>
                    <button
                      onClick={() => handleDisconnect(key)}
                      className="text-xs text-red-400/60 hover:text-red-400"
                    >
                      Trennen
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setShowConnect(key)}
                    className="text-xs px-3 py-1 bg-white/10 hover:bg-white/20 text-white/80 rounded"
                  >
                    Verbinden
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Connect Modal */}
      {showConnect && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-6 space-y-4">
          <h3 className="text-white font-medium">{availablePlatforms[showConnect]} verbinden</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-white/50">Account-Name</label>
              <input
                type="text"
                value={connectName}
                onChange={(e) => setConnectName(e.target.value)}
                placeholder={`@snapshotpro`}
                className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white text-sm mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-white/50">Access Token</label>
              <input
                type="password"
                value={connectToken}
                onChange={(e) => setConnectToken(e.target.value)}
                placeholder="Token einfügen"
                className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white text-sm mt-1 font-mono"
              />
            </div>
            {showConnect === 'facebook' && (
              <div>
                <label className="text-xs text-white/50">Page ID</label>
                <input
                  type="text"
                  value={connectExtra.page_id || ''}
                  onChange={(e) => setConnectExtra({...connectExtra, page_id: e.target.value})}
                  placeholder="Facebook Page ID"
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white text-sm mt-1"
                />
              </div>
            )}
            {showConnect === 'instagram' && (
              <div>
                <label className="text-xs text-white/50">Instagram User ID</label>
                <input
                  type="text"
                  value={connectExtra.ig_user_id || ''}
                  onChange={(e) => setConnectExtra({...connectExtra, ig_user_id: e.target.value})}
                  placeholder="Instagram Business User ID"
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white text-sm mt-1"
                />
              </div>
            )}
            {showConnect === 'pinterest' && (
              <div>
                <label className="text-xs text-white/50">Board ID</label>
                <input
                  type="text"
                  value={connectExtra.board_id || ''}
                  onChange={(e) => setConnectExtra({...connectExtra, board_id: e.target.value})}
                  placeholder="Pinterest Board ID"
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white text-sm mt-1"
                />
              </div>
            )}
            {showConnect === 'linkedin' && (
              <div>
                <label className="text-xs text-white/50">Author URN</label>
                <input
                  type="text"
                  value={connectExtra.author_urn || ''}
                  onChange={(e) => setConnectExtra({...connectExtra, author_urn: e.target.value})}
                  placeholder="urn:li:person:xxxxx"
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white text-sm mt-1"
                />
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => handleConnect(showConnect)}
                disabled={!connectToken}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-sm font-medium disabled:opacity-50"
              >
                Verbinden
              </button>
              <button
                onClick={() => { setShowConnect(null); setConnectToken(''); setConnectName(''); setConnectExtra({}); }}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white/80 rounded text-sm"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post Composer */}
      {connectedPlatforms.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-white/70 uppercase tracking-wider">Neuer Post</h3>
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 space-y-4">
            <textarea
              value={postText}
              onChange={(e) => setPostText(e.target.value)}
              rows={4}
              placeholder="Was möchten Sie posten?"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm resize-y"
            />

            <div>
              <label className="text-xs text-white/50">Bild-URL (optional)</label>
              <input
                type="text"
                value={postImageUrl}
                onChange={(e) => setPostImageUrl(e.target.value)}
                placeholder="https://snapshotpro.ai/storage/..."
                className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white text-sm mt-1"
              />
            </div>

            {postImageUrl && (
              <img src={postImageUrl} alt="Preview" className="h-32 rounded-lg object-cover" />
            )}

            <div>
              <label className="text-xs text-white/50 block mb-2">Plattformen</label>
              <div className="flex flex-wrap gap-2">
                {connectedPlatforms.map(p => (
                  <button
                    key={p}
                    onClick={() => togglePlatform(p)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                      selectedPlatforms.includes(p)
                        ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                        : `bg-white/5 ${PLATFORM_COLORS[p] || 'border-white/20 text-white/60'}`
                    }`}
                  >
                    {PLATFORM_ICONS[p]} {availablePlatforms[p]}
                  </button>
                ))}
                {connectedPlatforms.length > 1 && (
                  <button
                    onClick={() => setSelectedPlatforms(
                      selectedPlatforms.length === connectedPlatforms.length ? [] : [...connectedPlatforms]
                    )}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-white/5 border border-white/20 text-white/60 hover:bg-white/10"
                  >
                    {selectedPlatforms.length === connectedPlatforms.length ? 'Keine' : 'Alle'}
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={handlePost}
              disabled={posting || !postText.trim() || selectedPlatforms.length === 0}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold disabled:opacity-50 transition-all"
            >
              {posting ? 'Wird gepostet...' : `Auf ${selectedPlatforms.length} Plattform${selectedPlatforms.length !== 1 ? 'en' : ''} posten`}
            </button>
          </div>
        </div>
      )}

      {/* Post Historie */}
      {history.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-white/70 uppercase tracking-wider">Post-Historie</h3>
          <div className="space-y-2">
            {history.map((post, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <p className="text-white/80 text-sm flex-1 mr-4">{post.text?.substring(0, 120)}{post.text?.length > 120 ? '...' : ''}</p>
                  <span className="text-xs text-white/40 whitespace-nowrap">
                    {post.created_at ? new Date(post.created_at).toLocaleString('de-DE') : ''}
                  </span>
                </div>
                <div className="flex gap-2">
                  {Object.entries(post.results || {}).map(([platform, result]) => (
                    <span
                      key={platform}
                      className={`text-xs px-2 py-1 rounded ${
                        result.success
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                      title={result.error || result.post_id || ''}
                    >
                      {PLATFORM_ICONS[platform]} {result.success ? 'OK' : 'Fehler'}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
